import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../../components/ui/dropdown-menu';
import { Plus, Edit, Trash2, FileText, Search, RotateCcw, Pencil, AlertTriangle, DollarSign, Archive, Check, X, Eye, MoreVertical, ChevronUp, ChevronDown, ChevronsUpDown, Filter, Calendar, Building2, Hash, DollarSign as Dollar, Clock, FilterX, User, LayoutGrid, List } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [locallyDeletedQuoteIds, setLocallyDeletedQuoteIds] = useState(new Set()); // Track deleted quote IDs
  const [existingInvoices, setExistingInvoices] = useState([]); // Track existing invoices
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuotes, setTotalQuotes] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, approved, rejected, deleted
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    dateRange: { start: '', end: '' },
    amountRange: { min: '', max: '' },
    company: '',
    quoteNumber: '',
    status: 'all',
    createdBy: 'all'
  });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, quote: null });
  const [editDialog, setEditDialog] = useState({ isOpen: false, quote: null });
  const [editFormData, setEditFormData] = useState({
    status: 'pending'
  });
  const [activeActionsQuoteId, setActiveActionsQuoteId] = useState(null);
  const { get, post, put, delete: deleteApi } = useApi();
  const { can, PERMISSIONS } = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'

  useEffect(() => {
    if (user) {
      fetchQuotes();
      fetchExistingInvoices();
    }
  }, [currentPage, statusFilter, user]);

  // Set default view mode based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      setViewMode(isMobile ? 'grid' : 'table');
    };

    // Set initial view mode
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleSearch = (value) => {

    setSearchTerm(value);
  };

  const handleRefresh = () => {
    fetchQuotes();
    fetchExistingInvoices();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-primary" />
      : <ChevronDown className="h-4 w-4 text-primary" />;
  };

  const fetchExistingInvoices = async () => {
    try {
      // Try different API endpoints to see which one works
      let response;
      
      try {
        // First try the full endpoint with parameters
        response = await get('/invoices?search=&sortBy=createdAt&sortOrder=desc');
      } catch (firstError) {
        try {
          // Try basic endpoint
          response = await get('/invoices');
        } catch (secondError) {
          // Try admin endpoint
          response = await get('/admin/invoices');
        }
      }
      
      
      // Handle different response structures
      if (response?.success && Array.isArray(response?.data)) {
        setExistingInvoices(response.data);
      } else if (Array.isArray(response)) {
        setExistingInvoices(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setExistingInvoices(response.data);
      } else {
        setExistingInvoices([]);
      }
      
    } catch (error) {
      // Don't show error toast for this as it's not critical
      setExistingInvoices([]);
    }
  };

  // Check if a quote already has an invoice
  const hasInvoiceForQuote = (quoteNumber) => {
    return existingInvoices.some(invoice => 
      invoice.quoteNumber === quoteNumber || 
      invoice.quote_number === quoteNumber
    );
  };

  // Get the user's account ID for filtering
  const getUserAccountId = async () => {
    try {
      const response = await get('/accounts');
      if (response?.success && Array.isArray(response?.data)) {
        // Find the account that matches the current user's email
        const userAccount = response.data.find(account => 
          account.email === user?.email
        );
        return userAccount?._id;
      }
    } catch (error) {
    }
    return null;
  };

  const fetchQuotes = async () => {
    try {
      setIsLoading(true);
      
      console.group('📋 FETCHING QUOTES');
      
      let endpoint;
      
      // Use different endpoint for deleted quotes
      if (statusFilter === 'deleted') {
        endpoint = '/admin/quotes/trashed';
      } else {
        
        // Build query parameters for active quotes
        const params = new URLSearchParams({
          page: currentPage,
          perPage: perPage,
          sortBy: 'created_at',
          sortOrder: 'asc'
        });
        
        // Add user filter - only show quotes for the logged-in user (except for superadmin)
        const userId = user?._id || user?.id;
        const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'super_admin';
        
        // Add user filter - only show quotes for the logged-in user (except for superadmin)
        if (user && userId && !isSuperAdmin) {
          
          // Use user_id filter directly
          params.append('user_id', userId);
        } else if (isSuperAdmin) {
          // Superadmin: No filters applied - show all quotes from all accounts
        }
        
        // Only send status to API for pending, approved, rejected (not for 'all')
        if (statusFilter !== 'all') {
          params.append('status', statusFilter);
        }
        
        endpoint = `/admin/quotes?${params.toString()}`;
      }
      
      
      const response = await get(endpoint);
      
      
      
      // Log if no quotes found
      if (response?.data?.length === 0) {
        
        
       
      }
      
      
      
      // Handle the API response structure
      let quotesData = [];
      if (Array.isArray(response)) {
        // Direct array response (for deleted quotes endpoint)
        quotesData = response;
      } else if (response && Array.isArray(response?.data)) {
        // Wrapped response with data property
        quotesData = response.data;
      } else {
        setQuotes([]);
        return;
      }

      if (quotesData.length > 0) {
        // Map the response data
        const mappedQuotes = quotesData.map(quote => ({
          ...quote,
          // Map API field names to frontend expected names
          id: quote.id || quote._id,
          quotenumber: quote.quote_number || quote.quoteNumber,
          quote_number: quote.quote_number || quote.quoteNumber,
          total_amount: parseFloat(quote.total_amount || quote.amount) || 0,
          created_at: quote.created_at || quote.createdAt,
          updated_at: quote.updated_at || quote.updatedAt,
          // For deleted tab, all quotes are already deleted
          // For other tabs, mark as deleted if in locallyDeletedQuoteIds
          is_deleted: statusFilter === 'deleted' ? true : (locallyDeletedQuoteIds.has(quote.id || quote._id) || !!quote.deleted_at)
        }));
        
        // Additional client-side filtering by user (fallback if API doesn't filter)
        let userFilteredQuotes = mappedQuotes;
        const userId = user?._id || user?.id;
        const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'super_admin';
        
        if (user && userId && !isSuperAdmin) {
          userFilteredQuotes = mappedQuotes.filter(quote => {
            // Filter by creator ID or created_by field (using user_id)
            const isCreator = quote.creator?._id === userId || 
                            quote.creator?.id === userId ||
                            quote.created_by === userId ||
                            quote.creator_id === userId;
            
            return isCreator;
          });
          
          
        } else if (isSuperAdmin) {
          // Superadmin sees all quotes - no filtering applied
          userFilteredQuotes = mappedQuotes;
          
        }
        
        
        
        setQuotes(userFilteredQuotes);
        
        // Handle pagination data - only for non-deleted quotes
        if (statusFilter !== 'deleted') {
          setCurrentPage(response.meta?.currentPage || 1);
          setTotalPages(response.meta?.lastPage || 1);
          setTotalQuotes(response.meta?.total || 0);
          setPerPage(response.meta?.perPage || 10);
        } else {
          // For deleted quotes, set basic pagination
          setCurrentPage(1);
          setTotalPages(1);
          setTotalQuotes(userFilteredQuotes.length);
          setPerPage(10);
        }
      } else {
        // No quotes found
        setQuotes([]);
        setCurrentPage(1);
        setTotalPages(1);
        setTotalQuotes(0);
        setPerPage(10);
      }
      
    } catch (error) {
      console.group('❌ QUOTES FETCH ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while fetching quotes. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.status === 404) {
        errorMessage = 'API endpoint not found. Please check the server configuration.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setQuotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteDialog = (quote) => {
    setDeleteDialog({ isOpen: true, quote });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, quote: null });
  };

  const openEditDialog = (quote) => {
    setEditDialog({ isOpen: true, quote });
    setEditFormData({
      status: quote.status || 'pending'
    });
  };

  const closeEditDialog = () => {
    setEditDialog({ isOpen: false, quote: null });
    setEditFormData({
      status: 'pending'
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateQuote = async () => {
    if (!editDialog.quote) return;
    
    try {
      // API expects only status in the payload for status update
      const apiPayload = {
        status: editFormData.status
      };
      
      console.group('📝 UPDATING QUOTE STATUS');
      
      const response = await post(`/admin/quotes/${editDialog.quote.id}/status`, apiPayload);
      
      console.groupEnd();
      
      // Update the quote status in local state
      setQuotes(prevQuotes => 
        prevQuotes.map(quote => 
          quote.id === editDialog.quote.id 
            ? { ...quote, status: editFormData.status }
            : quote
        )
      );
      
      toast.success(response?.message || 'Quote status updated successfully');
      closeEditDialog();
    } catch (error) {
      console.group('❌ QUOTE UPDATE ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while updating the quote. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDeleteQuote = async () => {
    if (!deleteDialog.quote) return;
    
    try {
      console.group('🗑️ DELETING QUOTE');
      
      const response = await deleteApi(`/admin/quotes/${deleteDialog.quote.id}`);
      
      console.groupEnd();
      
      // Add quote ID to locally deleted set
      setLocallyDeletedQuoteIds(prev => {
        const newSet = new Set(prev);
        newSet.add(deleteDialog.quote.id);
        
        return newSet;
      });
      
      // Update the quote in local state to mark it as deleted
      setQuotes(prevQuotes => 
        prevQuotes.map(quote => 
          quote.id === deleteDialog.quote.id 
            ? { ...quote, deleted_at: new Date().toISOString(), is_deleted: true }
            : quote
        )
      );
      
      toast.success(
        response?.message || 'Quote deleted successfully! Click the "Deleted" tab to view and restore it.',
        { autoClose: 5000 }
      );
      closeDeleteDialog();
    } catch (error) {
      console.group('❌ QUOTE DELETE ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while deleting the quote. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleRestoreQuote = async (quoteId) => {
    try {
      console.group('♻️ RESTORING QUOTE');
      
      const response = await post(`/admin/quotes/${quoteId}/restore`, {});
      
      console.groupEnd();
      
      // Remove quote ID from locally deleted set
      setLocallyDeletedQuoteIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(quoteId);
        
        return newSet;
      });
      
      toast.success(
        response?.message || 'Quote restored successfully! Refreshing data...',
        { autoClose: 3000 }
      );
      
      // Auto refresh the data after successful restore
      await fetchQuotes();
      await fetchExistingInvoices();
      
    } catch (error) {
      console.group('❌ QUOTE RESTORE ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while restoring the quote. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleQuickApprove = async (quote) => {
    try {
      console.group('✅ QUICK APPROVE QUOTE');
      
      const payload = {
        status: 'approved',
        items: (quote.items || []).map(item => ({
          id: item._id,
          unit_price: parseFloat(item.unitPrice || item.unit_price),
        }))
      };
      
      
      const response = await post(`/admin/quotes/${quote.id}/approve`, payload);
      
      console.groupEnd();
      
      // Update the quote status in local state
      setQuotes(prevQuotes => 
        prevQuotes.map(q => 
          q.id === quote.id 
            ? { ...q, status: 'approved' }
            : q
        )
      );
      
      toast.success(response?.message || 'Quote approved successfully');
    } catch (error) {
      console.group('❌ QUOTE APPROVE ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while approving the quote. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleQuickReject = async (quote) => {
    try {
      console.group('❌ QUICK REJECT QUOTE');
      
      const payload = {
        status: 'rejected',
        items: (quote.items || []).map(item => ({
          id: item._id,
          unit_price: parseFloat(item.unitPrice || item.unit_price),
        }))
      };
      
      
      const response = await post(`/admin/quotes/${quote.id}/approve`, payload);
      
      console.groupEnd();
      
      // Update the quote status in local state
      setQuotes(prevQuotes => 
        prevQuotes.map(q => 
          q.id === quote.id 
            ? { ...q, status: 'rejected' }
            : q
        )
      );
      
      toast.success(response?.message || 'Quote rejected successfully');
    } catch (error) {
      console.group('❌ QUOTE REJECT ERROR');
      console.groupEnd();
      
      let errorMessage = 'An error occurred while rejecting the quote. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleViewQuote = (quote) => {
    // Navigate to QuoteDetails page with quote ID
    navigate(`/admin/quotes/${quote.id}`);
  };

  const handleGenerateInvoice = (quote) => {
    // Navigate to Add Invoice page with quote ID as a parameter
    navigate(`/admin/invoices/add?quoteId=${quote.id}`);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleAdvancedFilterChange = (field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value
      }
    }));
  };

  const handleAmountRangeChange = (field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      amountRange: {
        ...prev.amountRange,
        [field]: value
      }
    }));
  };

  const clearAllFilters = () => {
    setAdvancedFilters({
      dateRange: { start: '', end: '' },
      amountRange: { min: '', max: '' },
      company: '',
      quoteNumber: '',
      status: 'all',
      createdBy: 'all'
    });
    setSearchTerm('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (advancedFilters.dateRange.start || advancedFilters.dateRange.end) count++;
    if (advancedFilters.amountRange.min || advancedFilters.amountRange.max) count++;
    if (advancedFilters.company) count++;
    if (advancedFilters.quoteNumber) count++;
    if (advancedFilters.status !== 'all') count++;
    if (advancedFilters.createdBy !== 'all') count++;
    if (searchTerm) count++;
    return count;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Count deleted quotes - MUST be before filteredQuotes
  const deletedQuotesCount = quotes.filter(quote => quote.is_deleted).length;

  // Filter quotes based on search term, advanced filters, and deleted status (client-side filtering)
  const filteredQuotes = quotes.filter(quote => {
    // Search filter
    const matchesSearch = !searchTerm || 
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.quotenumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.account?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.status?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Advanced filters
    const matchesDateRange = !advancedFilters.dateRange.start || 
      new Date(quote.created_at) >= new Date(advancedFilters.dateRange.start);
    const matchesDateRangeEnd = !advancedFilters.dateRange.end || 
      new Date(quote.created_at) <= new Date(advancedFilters.dateRange.end);
    
    const matchesAmountRange = !advancedFilters.amountRange.min || 
      parseFloat(quote.total_amount || 0) >= parseFloat(advancedFilters.amountRange.min);
    const matchesAmountRangeMax = !advancedFilters.amountRange.max || 
      parseFloat(quote.total_amount || 0) <= parseFloat(advancedFilters.amountRange.max);
    
    const matchesCompany = !advancedFilters.company || 
      quote.account?.name?.toLowerCase().includes(advancedFilters.company.toLowerCase());
    
    const matchesQuoteNumber = !advancedFilters.quoteNumber || 
      quote.quote_number?.toLowerCase().includes(advancedFilters.quoteNumber.toLowerCase()) ||
      quote.quotenumber?.toLowerCase().includes(advancedFilters.quoteNumber.toLowerCase());
    
    const matchesStatus = advancedFilters.status === 'all' || 
      quote.status === advancedFilters.status;
    
    const matchesCreatedBy = advancedFilters.createdBy === 'all' || 
      quote.creator?.name?.toLowerCase().includes(advancedFilters.createdBy.toLowerCase()) ||
      quote.created_by === advancedFilters.createdBy;
    
    // Deleted filter based on active tab
    let matchesDeletedFilter = true;
    if (statusFilter === 'deleted') {
      // For deleted tab, server already returns only deleted quotes, so no additional filtering needed
      matchesDeletedFilter = true;
    } else if (statusFilter === 'all') {
      // Show all quotes (both deleted and non-deleted)
      matchesDeletedFilter = true;
    } else {
      // For pending, approved, rejected tabs - only show non-deleted quotes
      matchesDeletedFilter = !quote.is_deleted;
    }
    
    return matchesSearch && 
           matchesDateRange && 
           matchesDateRangeEnd && 
           matchesAmountRange && 
           matchesAmountRangeMax && 
           matchesCompany && 
           matchesQuoteNumber && 
           matchesStatus && 
           matchesCreatedBy && 
           matchesDeletedFilter;
  });

  // Sort the filtered quotes
  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'created_at':
        aValue = new Date(a.created_at || 0);
        bValue = new Date(b.created_at || 0);
        break;
      case 'quote_number':
        aValue = (a.quote_number || a.quotenumber || '').toLowerCase();
        bValue = (b.quote_number || b.quotenumber || '').toLowerCase();
        break;
      case 'company':
        aValue = (a.account?.name || '').toLowerCase();
        bValue = (b.account?.name || '').toLowerCase();
        break;
      case 'status':
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
        break;
      case 'amount':
        aValue = parseFloat(a.total_amount || 0);
        bValue = parseFloat(b.total_amount || 0);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Log filtering information for debugging
  

  // Skeleton loading component
  const SkeletonRow = () => (
    <TableRow className="h-8">
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-6 w-16 rounded-full" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <div className="flex items-center gap-0.5 sm:gap-1">
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
        </div>
      </TableCell>
    </TableRow>
  );

  // Show loading or no user message
  if (!user) {
    return (
      <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-muted-foreground mb-2">No User Logged In</h2>
            <p className="text-muted-foreground">Please log in to view your quotes.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {activeActionsQuoteId && (
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-40 pointer-events-none" />
      )}
      <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen relative">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start  sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">
            {user?.role === 'superadmin' || user?.role === 'super_admin' ? 'All Quotes' : 'My Quotes'}
          </h1>
          {sortField && (
            <p className="text-sm text-muted-foreground mt-1">
              Sorted by {sortField} ({sortDirection === 'asc' ? 'ascending' : 'descending'})
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
          <div className="relative flex-1 sm:flex-initial sm:w-64">
            <Input
              type="text"
              placeholder="Search quotes..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 border-1 border-muted-foreground/20"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="flex items-center gap-2 flex-shrink-0 border-1 border-muted-foreground/20 bg-muted/50"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          
          {/* Grid/Table Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
          >
            {viewMode === 'table' ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
            <span className="hidden sm:inline">{viewMode === 'table' ? 'Grid' : 'Table'}</span>
          </Button>
        </div>
      </div>

      {/* Tabs for Status Filtering - Desktop View */}
      <div className="hidden md:block">
        <Tabs value={statusFilter} onValueChange={handleStatusFilterChange} className="w-full mb-4">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="all">All Quotes</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="deleted" className="relative">
              Deleted
              {deletedQuotesCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                  {deletedQuotesCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Dropdown for Status Filtering - Mobile View */}
      <div className="md:hidden mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between border-1 border-muted-foreground/20 p-4">
              {statusFilter === 'all' && 'All Quotes'}
              {statusFilter === 'pending' && 'Pending'}
              {statusFilter === 'approved' && 'Approved'}
              {statusFilter === 'rejected' && 'Rejected'}
              {statusFilter === 'deleted' && (
                <span className="flex items-center gap-2">
                  Deleted
                  {deletedQuotesCount > 0 && (
                    <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                      {deletedQuotesCount}
                    </span>
                  )}
                </span>
              )}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-full">
            <DropdownMenuItem onClick={() => handleStatusFilterChange('all')}>
              All Quotes
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusFilterChange('pending')}>
              Pending
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusFilterChange('approved')}>
              Approved
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusFilterChange('rejected')}>
              Rejected
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusFilterChange('deleted')}>
              <span className="flex items-center gap-2">
                Deleted
                {deletedQuotesCount > 0 && (
                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                    {deletedQuotesCount}
                  </span>
                )}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

     

      {/* Table or Grid View */}
      {viewMode === 'table' ? (
      <div className="bg-card text-card-foreground rounded-sm overflow-x-auto">
        <Table className="text-xs sm:text-sm min-w-full border-1 border-muted-foreground/20">
          <TableHeader className="bg-muted/70 border-1 border-muted-foreground/20">
            <TableRow className="h-8 bg-muted/50">
              <TableHead 
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px] cursor-pointer hover:bg-muted/70 select-none"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Date
                  {getSortIcon('created_at')}
                </div>
              </TableHead>
              <TableHead 
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[140px] cursor-pointer hover:bg-muted/70 select-none"
                onClick={() => handleSort('quote_number')}
              >
                <div className="flex items-center gap-1">
                  Quote Number
                  {getSortIcon('quote_number')}
                </div>
              </TableHead>
              <TableHead 
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[140px] sm:min-w-[170px] hidden sm:table-cell cursor-pointer hover:bg-muted/70 select-none"
                onClick={() => handleSort('company')}
              >
                <div className="flex items-center gap-1">
                  Company
                  {getSortIcon('company')}
                </div>
              </TableHead>
              <TableHead 
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px] cursor-pointer hover:bg-muted/70 select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  Status
                  {getSortIcon('status')}
                </div>
              </TableHead>
              <TableHead 
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px] hidden md:table-cell cursor-pointer hover:bg-muted/70 select-none"
                onClick={() => handleSort('amount')}
              >
                <div className="flex items-center gap-1">
                  Amount
                  {getSortIcon('amount')}
                </div>
              </TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Show skeleton rows while loading
              Array.from({ length: 5 }).map((_, index) => (
                <SkeletonRow key={`skeleton-${index}`} />
              ))
            ) : sortedQuotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No quotes found
                </TableCell>
              </TableRow>
            ) : (
              // Show actual data when loaded
              sortedQuotes.map((quote) => (
                <TableRow key={quote.id} className={`h-8 ${quote.is_deleted ? 'opacity-60 bg-muted/30' : ''}`}>
                  <TableCell className="px-2 sm:px-3 py-2 cursor-pointer hover:text-primary">
                    <span className="text-xs sm:text-sm">
                      {new Date(quote.created_at).toLocaleDateString('en-GB')}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs sm:text-sm font-medium">
                        {quote.quotenumber}
                      </span>
                      {quote.is_deleted && (
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          Deleted
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
                    <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[140px]">
                      {quote.account?.name || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs sm:text-sm font-medium ${
                      quote.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                      quote.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                      quote.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100'
                    }`}>
                      {quote.status || 'Unknown'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2 text-xs sm:text-sm hidden md:table-cell">
                    {formatCurrency(quote.total_amount)}
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <div className="flex items-center gap-0.5 sm:gap-1">
                      {quote.is_deleted ? (
                        // Show restore button for deleted quotes - only for superadmin
                        (user?.role === 'superadmin' || user?.role === 'super_admin') ? (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRestoreQuote(quote.id)}
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-green-600 hover:text-green-700"
                            title="Restore quote"
                          >
                            <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground px-2 py-1">
                            Deleted
                          </span>
                        )
                      ) : (
                        // Show dropdown menu for actions
                        <DropdownMenu
                          onOpenChange={(open) =>
                            setActiveActionsQuoteId(open ? quote.id : null)
                          }
                        >
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-muted-foreground hover:text-foreground"
                              title="More actions"
                            >
                              <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 z-50 border-1 border-muted-foreground/20">
                            {/* View Quote */}
                            <DropdownMenuItem onClick={() => handleViewQuote(quote)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </DropdownMenuItem>
                            
                            {/* Generate Invoice - Only for approved quotes that don't have an invoice yet */}
                            {quote.status === 'approved' && !hasInvoiceForQuote(quote.quoteNumber) && (
                              <DropdownMenuItem onClick={() => handleGenerateInvoice(quote)}>
                                <FileText className="mr-2 h-4 w-4" />
                                Generate Invoice
                              </DropdownMenuItem>
                            )}
                            
                            {can(PERMISSIONS.APPROVE_QUOTE) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleQuickApprove(quote)}
                                  disabled={quote.status === 'approved'}
                                  className={quote.status === 'approved' ? 'text-gray-400 cursor-not-allowed' : ''}
                                >
                                  <Check className="mr-2 h-4 w-4" />
                                  Approve Quote
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleQuickReject(quote)}
                                  disabled={quote.status === 'approved'}
                                  className={quote.status === 'approved' ? 'text-gray-400 cursor-not-allowed' : ''}
                                >
                                  <X className="mr-2 h-4 w-4" />
                                  Reject Quote
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => openEditDialog(quote)}
                                  disabled={quote.status === 'approved'}
                                  className={quote.status === 'approved' ? 'text-gray-400 cursor-not-allowed' : ''}
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit Status
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {/* Delete button - available for both superadmin and admin */}
                            {can(PERMISSIONS.DELETE_QUOTE) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => openDeleteDialog(quote)}
                                  disabled={quote.status === 'approved'}
                                  className={`text-red-600 hover:text-red-700 ${
                                    quote.status === 'approved' ? 'text-gray-400 cursor-not-allowed' : ''
                                  }`}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Quote
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isLoading ? (
            /* Skeleton Grid */
            Array.from({ length: 8 }).map((_, index) => (
              <div key={`skeleton-${index}`} className="bg-card text-card-foreground rounded-sm overflow-x-auto border-1 border-muted-foreground/20">
                <div className="p-4 space-y-3">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-6 w-16 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                </div>
              </div>
            ))
          ) : sortedQuotes.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No quotes found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first quote'}
              </p>
            </div>
          ) : (
            sortedQuotes.map((quote) => (
              <div 
                key={quote.id} 
                className={`bg-card text-card-foreground rounded-sm overflow-hidden border-1 border-muted-foreground/20 hover:shadow-lg transition-all duration-300 hover:border-primary/20 group bg-muted/50 ${quote.is_deleted ? 'opacity-60' : ''}`}
              >
                <div className="p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-1">Quote Number</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold truncate">
                          {quote.quotenumber}
                        </p>
                        {quote.is_deleted && (
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            Deleted
                          </span>
                        )}
                      </div>
                    </div>
                    <DropdownMenu
                      onOpenChange={(open) =>
                        setActiveActionsQuoteId(open ? quote.id : null)
                      }
                    >
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 z-50 border-1 border-muted-foreground/20">
                        <DropdownMenuItem onClick={() => handleViewQuote(quote)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        
                        {quote.status === 'approved' && !hasInvoiceForQuote(quote.quoteNumber) && (
                          <DropdownMenuItem onClick={() => handleGenerateInvoice(quote)}>
                            <FileText className="mr-2 h-4 w-4" />
                            Generate Invoice
                          </DropdownMenuItem>
                        )}
                        
                        {!quote.is_deleted && can(PERMISSIONS.APPROVE_QUOTE) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleQuickApprove(quote)}
                              disabled={quote.status === 'approved'}
                              className={quote.status === 'approved' ? 'text-gray-400 cursor-not-allowed' : ''}
                            >
                              <Check className="mr-2 h-4 w-4" />
                              Approve Quote
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleQuickReject(quote)}
                              disabled={quote.status === 'approved'}
                              className={quote.status === 'approved' ? 'text-gray-400 cursor-not-allowed' : ''}
                            >
                              <X className="mr-2 h-4 w-4" />
                              Reject Quote
                            </DropdownMenuItem>
                          </>
                        )}
                        
                        {!quote.is_deleted && can(PERMISSIONS.DELETE_QUOTE) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => openDeleteDialog(quote)}
                              disabled={quote.status === 'approved'}
                              className={`text-red-600 hover:text-red-700 ${
                                quote.status === 'approved' ? 'text-gray-400 cursor-not-allowed' : ''
                              }`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Quote
                            </DropdownMenuItem>
                          </>
                        )}

                        {quote.is_deleted && (user?.role === 'superadmin' || user?.role === 'super_admin') && (
                          <DropdownMenuItem onClick={() => handleRestoreQuote(quote.id)}>
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Restore Quote
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Company */}
                  {quote.account?.name && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Company</p>
                      <p className="text-sm truncate">
                        {quote.account.name}
                      </p>
                    </div>
                  )}

                  {/* Amount */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Total Amount</p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(quote.total_amount)}
                    </p>
                  </div>

                  {/* Date & Status */}
                  <div className="flex items-center justify-between pt-2 border-t border-muted-foreground/10">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(quote.created_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      quote.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                      quote.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                      quote.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100'
                    }`}>
                      {quote.status || 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4">
        {isLoading ? (
          <>
            <Skeleton className="h-5 w-48" />
            <div className="flex items-center gap-2 flex-wrap">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-12" />
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              Showing page {currentPage} of {totalPages} | Total: {totalQuotes} quotes
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="hover:bg-muted/50"
              >
                Previous
              </Button>
              {/* Show page 1 if not in range */}
              {currentPage > 2 && totalPages > 3 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    className={`${
                      currentPage === 1 
                        ? '!bg-black !text-white dark:!bg-white dark:!text-black font-semibold hover:!bg-black/90 dark:hover:!bg-white/90 border-black dark:border-white' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    1
                  </Button>
                  {currentPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
                </>
              )}
              {/* Show pages around current page */}
              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return page;
              })
              .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
              .filter(page => page >= 1 && page <= totalPages) // Ensure valid range
              .map(page => (
                <Button
                  key={page}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={`${
                    currentPage === page 
                      ? '!bg-black !text-white dark:!bg-white dark:!text-black font-semibold hover:!bg-black/90 dark:hover:!bg-white/90 border-black dark:border-white' 
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  {page}
                </Button>
              ))}
              {/* Show last page if not in range */}
              {currentPage < totalPages - 2 && totalPages > 3 && (
                <>
                  {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                    className={`${
                      currentPage === totalPages 
                        ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                >
                  {totalPages}
                </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="hover:bg-muted/50"
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border border-border/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Delete Quote
                </h3>
                <p className="text-sm text-muted-foreground">
                  You can restore it from the Deleted tab
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to delete quote{' '}
                <span className="font-semibold text-foreground">
                  "{deleteDialog.quote?.quote_number}"
                </span>?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                The quote will be marked as deleted and can be restored later using the restore button.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeDeleteDialog}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteQuote}
                className="px-4 py-2 bg-red-600 hover:bg-red-700"
              >
                Delete Quote
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quote Sheet */}
      <Sheet open={editDialog.isOpen} onOpenChange={(open) => !open && closeEditDialog()}>
        <SheetContent className="w-[400px] sm:w-[540px] p-3">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Pencil className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              Update Quote Status
            </SheetTitle>
            <SheetDescription>
              Change the status of this quote
            </SheetDescription>
          </SheetHeader>
          
          <div className="space-y-4 mt-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Quote Number
                </label>
                <Input
                  value={editDialog.quote?.quote_number || ''}
                  disabled
                  className="w-full bg-muted/50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Company
                </label>
                <Input
                  value={editDialog.quote?.account?.name || ''}
                  disabled
                  className="w-full bg-muted/50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Total Amount (Read-only)
                </label>
                <Input
                  value={formatCurrency(editDialog.quote?.total_amount || 0)}
                  disabled
                  className="w-full bg-muted/50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={editFormData.status}
                  onChange={(e) => handleEditFormChange('status', e.target.value)}
                  className="w-full px-3 py-2 rounded-md text-foreground bg-muted/50 focus:outline-none"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 justify-end mt-6">
            <Button
              variant="outline"
              onClick={closeEditDialog}
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleUpdateQuote}
              className="px-4 py-2"
            >
              Update Status
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      </div>
    </>
  );
}
